import React from 'react';
import { Navigate, Route } from 'react-router-dom';
import RequireCapability, { SettingsSkeleton } from 'src/components/Base/RequireCapability';
import PlanLock from 'src/components/Billing/PlanLock';
import MainLayout from 'src/components/Layout/MainLayout';
import SettingsLayout from 'src/components/Layout/SettingsLayout';
import { useSettingsNav } from 'src/hooks/useSettingsNav';
import EmployeeDayOff from 'src/pages/EmployeeDayOff';
import EmployeeRules from 'src/pages/EmployeeRules';
import Integrations from 'src/pages/Integrations';
import IntegrationsZalo from 'src/pages/Integrations/Zalo';
import MainMenuSettings from 'src/pages/MainMenuSettings';
import MenuItemVariantsSettings from 'src/pages/MenuItemVariantsSettings';
import MenuItemsSettings from 'src/pages/MenuItemsSettings';
import OrgAuditLog from 'src/pages/OrgSettings/AuditLog';
import OrgBilling from 'src/pages/OrgSettings/Billing';
import OrgGeneral from 'src/pages/OrgSettings/General';
import OrgInvitations from 'src/pages/OrgSettings/Invitations';
import OrgMembers from 'src/pages/OrgSettings/Members';
import RestaurantBranchesSettings from 'src/pages/RestaurantBranchesSettings';
import RestaurantInformationSettings from 'src/pages/RestaurantInformationSettings';
import RestaurantTablesSettings from 'src/pages/RestaurantTablesSettings';
import TimesheetSettings from 'src/pages/TimesheetSettings';
import TransactionCategories from 'src/pages/TransactionCategories';

// Extracted from `App.tsx` so a unit test can import the settings route tree without pulling in
// every other route group (and their transitive dependencies, some of which — e.g. lottie-web —
// do not survive a jsdom test environment). See `src/config/settingsRoutes.test.ts`.
function SettingsIndexRedirect() {
  const { ready, firstAllowedPath } = useSettingsNav();
  if (!ready) {
    return <SettingsSkeleton />;
  }
  return <Navigate to={firstAllowedPath} replace />;
}

export const settingsLeafRoutes = (
  <Route element={<MainLayout />}>
    <Route path="settings" element={<SettingsLayout />}>
      <Route index element={<SettingsIndexRedirect />} />
      <Route path="*" element={<SettingsIndexRedirect />} />
      <Route
        path="organization"
        element={
          <RequireCapability capability="canManageOrg">
            <OrgGeneral />
          </RequireCapability>
        }
      />
      <Route
        path="organization/members"
        element={
          <RequireCapability capability="canManageOrg">
            <OrgMembers />
          </RequireCapability>
        }
      />
      <Route
        path="organization/invitations"
        element={
          <RequireCapability capability="canManageOrg">
            <OrgInvitations />
          </RequireCapability>
        }
      />
      <Route
        path="organization/audit"
        element={
          <RequireCapability capability="canManageOrg">
            <PlanLock feature="auditLog">
              <OrgAuditLog />
            </PlanLock>
          </RequireCapability>
        }
      />
      <Route
        path="organization/billing"
        element={
          <RequireCapability capability="canManageOrg">
            <OrgBilling />
          </RequireCapability>
        }
      />
      <Route
        path="restaurant"
        element={
          <RequireCapability capability="canManageRestaurantSettings">
            <RestaurantInformationSettings />
          </RequireCapability>
        }
      />
      <Route
        path="restaurant/branches"
        element={
          <RequireCapability capability="canManageRestaurantSettings">
            <RestaurantBranchesSettings />
          </RequireCapability>
        }
      />
      <Route
        path="restaurant/tables"
        element={
          <RequireCapability capability="canManageRestaurantSettings">
            <RestaurantTablesSettings />
          </RequireCapability>
        }
      />
      <Route
        path="restaurant/timesheets"
        element={
          <RequireCapability capability="canManageRestaurantSettings">
            <TimesheetSettings />
          </RequireCapability>
        }
      />
      <Route
        path="menu"
        element={
          <RequireCapability capability="canManageRestaurantSettings">
            <MainMenuSettings />
          </RequireCapability>
        }
      />
      <Route
        path="menu/items"
        element={
          <RequireCapability capability="canManageRestaurantSettings">
            <MenuItemsSettings />
          </RequireCapability>
        }
      />
      <Route
        path="menu/item-variants"
        element={
          <RequireCapability capability="canManageRestaurantSettings">
            <MenuItemVariantsSettings />
          </RequireCapability>
        }
      />
      <Route
        path="accounting/categories"
        element={
          <RequireCapability capability="canManageRestaurantSettings">
            <TransactionCategories />
          </RequireCapability>
        }
      />
      <Route
        path="employees/rules"
        element={
          <RequireCapability capability="canManageRestaurantSettings">
            <EmployeeRules />
          </RequireCapability>
        }
      />
      <Route
        path="employees/day-off"
        element={
          <RequireCapability capability="canManageRestaurantSettings">
            <EmployeeDayOff />
          </RequireCapability>
        }
      />
      <Route
        path="integrations"
        element={
          <RequireCapability capability="canManageOrg">
            <Integrations />
          </RequireCapability>
        }
      >
        <Route
          path="zalo"
          element={
            <PlanLock feature="zaloOrdering">
              <IntegrationsZalo />
            </PlanLock>
          }
        />
      </Route>
    </Route>
  </Route>
);
